import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, flexRender, createColumnHelper, SortingState, ColumnFiltersState, } from '@tanstack/react-table'
import { formatDateWithDateAndMonthName, getValueFromResource } from '../../common/timeLiveFunction'
import { KTIcon } from '../../_metronic/helpers'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './Geofencemodal.css'
import ActivitiesRecorded from '../../assets/image/ActivitiesRecorded.svg'
import InsideGeofence from '../../assets/image/InsideGeofence.svg'
import IOutsideGeofence from '../../assets/image/OutsideGeofence.svg'
import ComplianceScore from '../../assets/image/ComplianceScore.svg'
import AttendanceSource from '../../assets/image/AttendanceSource.svg'
import { format12HoursOnly } from '../../common/UiUtilities'
import { Tooltip as ReactTooltip } from 'react-tooltip';
import moment from 'moment'

interface PolygonPoint {
    Latitude: number
    Longitude: number
    PointOrder?: number
    PointLabel?: string
}

interface ZoneListItem {
    AccountGeoFenceZoneId: number
    ZoneName: string
    Radius: number
    RadiusUnit: string
    Latitude: number
    Longitude: number
    BoundaryType: number
    PolygonPoints?: PolygonPoint[] | [number, number][] | string
}

interface SummaryData {
    AccountEmployeeId: number
    EmployeeCode: string
    EmployeeName: string
    DepartmentName: string
    ShiftName: string
    AttendanceDate: string
    AttendanceSource: string
    ComplianceScorePercent?: number
    ActivitiesRecorded?: number
    InsideGeofenceCount?: number
    OutsideGeofenceCount?: number
    TotalDistance: number
    AttendanceZoneList: ZoneListItem[]
}

type ActivityStatus = 'Inside' | 'Outside' | string

interface ActivityItem {
    Id: number
    AccountEmployeeAttendanceId: number
    Time: string
    Activity: string
    Source: string
    Status: ActivityStatus
    Geofence: string
    Distance: number
    Latitude: number
    Longitude: number
    DeviceInformation?: string
    RadiusUnit?: string
    BoundaryType?: number
}

interface ResolvedZone {
    id: number
    name: string
    boundaryType: number
    center: [number, number]
    radius: number
    path: [number, number][]
    color: string
}

const TOOLTIP_STYLE = {
    fontSize: 12, fontWeight: 'normal', padding: 8, borderRadius: 6,
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    backgroundColor: 'black', color: 'white', zIndex: 999999,
    maxWidth: 220, whiteSpace: 'normal' as const,
};

const DEFAULT_RADIUS_M = 150
const ZONE_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#c2410c', '#4d7c0f', '#be185d']
const FALLBACK_CENTER: [number, number] = [24.8615, 67.0099]
const STREET_TILE = 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
const BOUNDARY_TYPE_CIRCLE = 1
const BOUNDARY_TYPE_POLYGON = 2

function isValidCoord(c: [number, number] | null | undefined) {
    return (
        c != null &&
        typeof c[0] === 'number' &&
        isFinite(c[0]) &&
        typeof c[1] === 'number' &&
        isFinite(c[1]) &&
        c[0] >= -90 &&
        c[0] <= 90 &&
        c[1] >= -180 &&
        c[1] <= 180
    )
}

function isValidPolygonCoordinate(c: [number, number]) {
    return isValidCoord(c) && !(c[0] === 0 && c[1] === 0)
}

function distanceMeters(a: [number, number], b: [number, number]) {
    const R = 6371000
    const dLat = ((b[0] - a[0]) * Math.PI) / 180
    const dLng = ((b[1] - a[1]) * Math.PI) / 180
    const lat1 = (a[0] * Math.PI) / 180
    const lat2 = (b[0] * Math.PI) / 180
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(h))
}

function formatDistance(m: number) {
    if (m == null || !isFinite(m)) return '—';
    return m >= 1000 ? `${(m / 1000)?.toFixed(2)} km` : `${m?.toFixed(2)} m`
}

const getRadiusForMap = (radius: number, unit?: string) => {
    if (!isFinite(radius)) {
        return DEFAULT_RADIUS_M
    }

    const normalizedUnit = unit?.toLowerCase()

    if (normalizedUnit === 'feet' || normalizedUnit === 'ft') {
        return radius * 0.3048
    }

    if (normalizedUnit === 'miles' || normalizedUnit === 'mi') {
        return radius * 1609.344
    }

    if (normalizedUnit === 'kilometers' || normalizedUnit === 'km') {
        return radius * 1000
    }

    return radius
}

function parsePolygonPoints(raw: any): [number, number][] {
    if (!raw) {
        return []
    }

    let points = raw

    if (typeof raw === 'string') {
        try {
            points = JSON.parse(raw)
        } catch {
            return []
        }
    }

    if (points && typeof points === 'object' && !Array.isArray(points) && Array.isArray(points.PolygonPoints)) {
        points = points.PolygonPoints
    }

    if (points && typeof points === 'object' && !Array.isArray(points) && Array.isArray(points.Coordinates)) {
        points = points.Coordinates
    }

    if (!Array.isArray(points)) {
        return []
    }

    return points.map((p: any) => {
        if (Array.isArray(p)) {
            const latitude = Number(p[0])
            const longitude = Number(p[1])

            if (isFinite(latitude) && isFinite(longitude)) {
                return {
                    point: [latitude, longitude] as [number, number],
                    order: 0,
                }
            }

            return null
        }

        if (p && typeof p === 'object') {
            const latitude = Number(p.Latitude ?? p.latitude ?? p.lat)
            const longitude = Number(p.Longitude ?? p.longitude ?? p.lng ?? p.lon)
            const order = Number(p.PointOrder ?? 0)

            if (isFinite(latitude) && isFinite(longitude)) {
                return {
                    point: [latitude, longitude] as [number, number],
                    order,
                }
            }
        }

        return null
    })
        .filter((item): item is {
            point: [number, number]
            order: number
        } => !!item && isValidPolygonCoordinate(item.point)
        )
        .sort((a, b) => a.order - b.order)
        .map(item => item.point)
}

function isPointInsidePolygon(point: [number, number], polygon: [number, number][]) {
    if (polygon.length < 3) {
        return false
    }

    const x = point[1]
    const y = point[0]
    let inside = false

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][1]
        const yi = polygon[i][0]
        const xj = polygon[j][1]
        const yj = polygon[j][0]
        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi

        if (intersect) {
            inside = !inside
        }
    }

    return inside
}

function toLocalMeters(point: [number, number], referenceLatitude: number): [number, number] {
    const earthRadius = 6371000
    const lat = (point[0] * Math.PI) / 180
    const lng = (point[1] * Math.PI) / 180
    const refLat = (referenceLatitude * Math.PI) / 180
    return [earthRadius * lng * Math.cos(refLat), earthRadius * lat]
}

function pointToSegmentDistanceMeters(point: [number, number], segmentStart: [number, number], segmentEnd: [number, number]) {
    const referenceLatitude = point[0]
    const p = toLocalMeters(point, referenceLatitude)
    const a = toLocalMeters(segmentStart, referenceLatitude)
    const b = toLocalMeters(segmentEnd, referenceLatitude)
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    if (dx === 0 && dy === 0) {
        return Math.sqrt((p[0] - a[0]) ** 2 + (p[1] - a[1]) ** 2)
    }

    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy)
    const clampedT = Math.max(0, Math.min(1, t))
    const closestX = a[0] + clampedT * dx
    const closestY = a[1] + clampedT * dy
    return Math.sqrt((p[0] - closestX) ** 2 + (p[1] - closestY) ** 2)
}

function distanceFromPolygonMeters(point: [number, number], polygon: [number, number][]) {
    if (polygon.length < 3) {
        return NaN
    }

    if (isPointInsidePolygon(point, polygon)) {
        return 0
    }

    let minimumDistance = Infinity

    for (let i = 0; i < polygon.length; i++) {
        const start = polygon[i]
        const end = polygon[(i + 1) % polygon.length]
        const distance = pointToSegmentDistanceMeters(point, start, end)
        minimumDistance = Math.min(minimumDistance, distance)
    }

    return minimumDistance
}

function getActivityZoneDistance(activity: ActivityItem, zone?: ResolvedZone) {
    if (!zone) {
        return activity.Distance
    }

    const activityPoint: [number, number] = [Number(activity.Latitude), Number(activity.Longitude)]

    if (!isValidCoord(activityPoint)) {
        return NaN
    }

    if (zone.boundaryType === BOUNDARY_TYPE_POLYGON && zone.path.length >= 3) {
        return distanceFromPolygonMeters(activityPoint, zone.path)
    }

    return distanceMeters(zone.center, activityPoint)
}

function resolveZones(zoneList: ZoneListItem[], activities: ActivityItem[]): ResolvedZone[] {
    const validActivities = activities.filter(a =>
        isValidCoord([Number(a.Latitude), Number(a.Longitude)])
    )

    const overallCenter: [number, number] = validActivities.length
        ? [
            validActivities.reduce((sum, a) => sum + Number(a.Latitude), 0) /
            validActivities.length,

            validActivities.reduce((sum, a) => sum + Number(a.Longitude), 0) /
            validActivities.length,
        ]
        : FALLBACK_CENTER

    return zoneList.map((z, idx) => {
        const boundaryType = Number(z.BoundaryType)
        const isPolygon = boundaryType === BOUNDARY_TYPE_POLYGON
        const path = isPolygon ? parsePolygonPoints(z.PolygonPoints) : []
        const validPolygon = isPolygon && path.length >= 3
        let center: [number, number]

        if (validPolygon) {
            center = path[0]
        } else {
            const zoneLatitude = Number(z.Latitude)
            const zoneLongitude = Number(z.Longitude)
            const zoneCenter: [number, number] = [zoneLatitude, zoneLongitude]
            const validZoneCenter = isValidCoord(zoneCenter) && !(zoneCenter[0] === 0 && zoneCenter[1] === 0)
            center = validZoneCenter ? zoneCenter : overallCenter
        }

        const validRadius = Number(z.Radius)
        const radius = isFinite(validRadius) && validRadius > 0 ? getRadiusForMap(validRadius, z.RadiusUnit) : DEFAULT_RADIUS_M

        return {
            id: Number(z.AccountGeoFenceZoneId),
            name: z.ZoneName ?? 'Unknown Zone',
            boundaryType: validPolygon ? BOUNDARY_TYPE_POLYGON : BOUNDARY_TYPE_CIRCLE,
            center,
            radius: validPolygon ? 0 : radius,
            path: validPolygon ? path : [],
            color: ZONE_COLORS[idx % ZONE_COLORS.length],
        }
    })
}

const columnHelper = createColumnHelper<ActivityItem>()

const GeoMap: React.FC<{ zones: ResolvedZone[],  activities: ActivityItem[],  selected: ActivityItem | null,  onSelect: (a: ActivityItem) => void}> = ({ zones, activities, selected, onSelect }) => {
    const ref = useRef<HTMLDivElement>(null)
    const mapRef = useRef<L.Map | null>(null)
    const layersRef = useRef<L.Layer[]>([])
    const fitDoneRef = useRef(false)

    useEffect(() => {
        if (mapRef.current || !ref.current) return
        const map = L.map(ref.current, { zoomControl: true, attributionControl: false })
        L.tileLayer(STREET_TILE, { maxZoom: 19 }).addTo(map)
        map.setView(FALLBACK_CENTER, 13)
        mapRef.current = map
        setTimeout(() => {
            map.invalidateSize()
        }, 100)

        return () => {
            map.remove()
            mapRef.current = null
        }
    }, [])

    useEffect(() => {
        fitDoneRef.current = false
    }, [zones, activities])

    useEffect(() => {
        const map = mapRef.current
        if (!map) return
        layersRef.current.forEach(layer => {
            try {
                layer.remove()
            } catch { }
        })

        layersRef.current = []

        zones?.forEach(zone => {
            let shape: L.Layer

            if (zone.boundaryType === BOUNDARY_TYPE_POLYGON && zone.path.length >= 3) {
                shape = L.polygon(zone.path, {
                    color: zone.color,
                    weight: 2,
                    dashArray: '8,5',
                    fillColor: zone.color,
                    fillOpacity: 0.12,
                }).addTo(map)
            } else {
                shape = L.circle(zone.center, {
                    radius: zone.radius,
                    color: zone.color,
                    weight: 2,
                    dashArray: '8,5',
                    fillColor: zone.color,
                    fillOpacity: 0.12,
                }).addTo(map)
            }

            const marker = L.marker(zone.center)
                .addTo(map)
                .bindTooltip(zone.name, {
                    permanent: true,
                    direction: 'top',
                    offset: [-16, -14],
                    className: 'gf-zone-tooltip',
                })

            layersRef.current.push(shape, marker)
        })

        const sortedActivities = [...activities].sort(
            (a, b) => new Date(a.Time).getTime() - new Date(b.Time).getTime()
        )

        const pts = sortedActivities
            .filter(a => isValidCoord([Number(a.Latitude), Number(a.Longitude)]))
            .map(a => [Number(a.Latitude), Number(a.Longitude)] as [number, number])

        if (pts.length > 1) {
            const route = L.polyline(pts, {
                color: '#94a3b8',
                weight: 1.5,
                dashArray: '4,4',
                opacity: 0.6,
            }).addTo(map)

            layersRef.current.push(route)
        }

        const STATUS_COLOR: Record<string, string> = {
            Inside: '#12a05c',
            Outside: '#e03a3a',
        }

        activities.forEach(act => {
            const pos: [number, number] = [Number(act.Latitude), Number(act.Longitude)]

            if (!isValidCoord(pos)) {
                return
            }

            const col = STATUS_COLOR[act.Status] ?? '#9ca3af'

            const isSel = selected?.Id === act.Id

            const circleMarker = L.circleMarker(pos, {
                radius: isSel ? 13 : 10,
                fillColor: col,
                color: col,
                weight: isSel ? 3 : 2,
                fillOpacity: 1,
            }).addTo(map)

            const labelMarker = L.marker(pos, {
                icon: L.divIcon({
                    className: '',
                    html: `<div style="background:${col};color:#fff;font-size:11px;width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;pointer-events:none">${act.Id}</div>`,
                    iconAnchor: [8, 8],
                }),
                zIndexOffset: 1000,
            }).addTo(map)

            circleMarker.bindTooltip(
                `<b>#${act.Id} ${act.Activity}</b><br/>${format12HoursOnly(act.Time, 0)}`,
                {
                    direction: 'top',
                    offset: [0, -10],
                }
            )

            circleMarker.on('click', () => onSelect(act))
            labelMarker.on('click', () => onSelect(act))
            layersRef.current.push(circleMarker, labelMarker)
        })

        if (!fitDoneRef.current) {
            const allPts: [number, number][] = [
                ...zones.flatMap(zone => {
                    if (zone.boundaryType === BOUNDARY_TYPE_POLYGON && zone.path.length >= 3) {
                        return zone.path
                    }

                    return [zone.center]
                }),

                ...activities
                    .filter(a => isValidCoord([Number(a.Latitude), Number(a.Longitude)]))
                    .map(a => [Number(a.Latitude), Number(a.Longitude)] as [number, number]),
            ]

            if (allPts.length) {
                map.fitBounds(L.latLngBounds(allPts), {
                    padding: [40, 40],
                    maxZoom: 17,
                })

                fitDoneRef.current = true
            }
        }

        setTimeout(() => {
            map.invalidateSize()
        }, 100)
    }, [zones, activities, selected, onSelect])

    useEffect(() => {
        if (!mapRef.current || !selected) {
            return
        }

        if (!isValidCoord([Number(selected.Latitude), Number(selected.Longitude)])) {
            return
        }

        mapRef.current.flyTo([Number(selected.Latitude), Number(selected.Longitude)], 17, {
            duration: 0.6,
        })
    }, [selected])

    return (
        <div
            ref={ref}
            style={{
                width: '100%',
                height: '100%',
            }}
        />
    )
}

const PreviewMap: React.FC<{ zone: ResolvedZone | undefined, activity: ActivityItem}> = ({ zone, activity }) => {
    const ref = useRef<HTMLDivElement>(null)
    const mapRef = useRef<L.Map | null>(null)

    useEffect(() => {
        if (mapRef.current || !ref.current) {
            return
        }

        const map = L.map(ref.current, {
            zoom: 12,
            zoomControl: false,
            attributionControl: false,
            dragging: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            keyboard: false,
        })

        L.tileLayer(STREET_TILE, {
            maxZoom: 19,
        }).addTo(map)

        map.setView(FALLBACK_CENTER, 15)

        mapRef.current = map

        setTimeout(() => {
            map.invalidateSize()
        }, 100)

        return () => {
            map.remove()
            mapRef.current = null
        }
    }, [])

    useEffect(() => {
        const map = mapRef.current

        if (!map) {
            return
        }

        map.eachLayer(layer => {
            if (!(layer instanceof L.TileLayer)) {
                layer.remove()
            }
        })

        const activityPoint: [number, number] = [
            Number(activity.Latitude),
            Number(activity.Longitude),
        ]

        if (!isValidCoord(activityPoint)) {
            return
        }

        const bounds = L.latLngBounds([])

        if (zone) {
            if (zone.boundaryType === BOUNDARY_TYPE_POLYGON && zone.path.length >= 3) {
                const polygon = L.polygon(zone.path, {
                    color: zone.color,
                    weight: 2,
                    dashArray: '5,4',
                    fillColor: zone.color,
                    fillOpacity: 0.12,
                }).addTo(map)

                bounds.extend(polygon.getBounds())

                L.marker(zone.center)
                    .addTo(map)
                    .bindTooltip(zone.name, {
                        permanent: true,
                        direction: 'top',
                        offset: [-16, -14],
                        className: 'gf-zone-tooltip',
                    })

                bounds.extend(zone.center)
            } else {
                const circle = L.circle(zone.center, {
                    radius: zone.radius,
                    color: zone.color,
                    weight: 2,
                    dashArray: '5,4',
                    fillColor: zone.color,
                    fillOpacity: 0.12,
                }).addTo(map)

                bounds.extend(circle.getBounds())

                L.marker(zone.center)
                    .addTo(map)
                    .bindTooltip(zone.name, {
                        permanent: true,
                        direction: 'top',
                        offset: [0, -32],
                        className: 'gf-zone-tooltip',
                    })
            }
        }

        const col = activity.Status === 'Inside' ? '#12a05c' : activity.Status === 'Outside' ? '#e03a3a' : '#9ca3af'

        L.circleMarker(activityPoint, {
            radius: 8,
            fillColor: col,
            color: '#fff',
            weight: 2,
            fillOpacity: 1,
        }).addTo(map)

        bounds.extend(activityPoint)

        if (bounds.isValid()) {
            map.fitBounds(bounds, {
                padding: [20, 20],
                maxZoom: 17,
            })
        } else {
            map.setView(activityPoint, 15)
        }

        setTimeout(() => {
            map.invalidateSize()
        }, 100)
    }, [activity, zone])

    return (
        <div
            ref={ref}
            className='gf-preview'
            style={{ width: '100%', height: 220 }}
        />
    )
}

const RightPanel: React.FC<{ selected: ActivityItem | null, zones: ResolvedZone[] }> = ({ selected, zones }) => {
    const [advOpen, setAdvOpen] = useState(true)
    const zone = selected ? zones.find(z => z.name === selected.Geofence) : undefined
    const calculatedDistance = selected && zone ? getActivityZoneDistance(selected, zone) : selected?.Distance
    const distance = calculatedDistance !== undefined && isFinite(calculatedDistance) ? formatDistance(calculatedDistance) : '—'

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                height: '100%',
            }}
        >
            <div className='gf-panel'>
                <div className='gf-panel-hd'>
                    <span className='gf-value'>Selected Activity Details</span>
                </div>

                {!selected ? (
                    <div
                        className='gf-panel-bd'
                        style={{
                            color: '#9ca3af',
                            fontSize: '0.76rem',
                            textAlign: 'center',
                            padding: '22px 14px',
                        }}
                    >
                        Please click on any point of map.
                    </div>
                ) : (
                    <div className='gf-panel-bd'>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 10,
                            }}
                        >
                            <div className='gf-detail-activity-circle'
                                style={{
                                    background: selected.Status === 'Inside' ? '#e6f9f0' : '#fff0f0',
                                    color: selected.Status === 'Inside' ? '#12a05c' : '#e03a3a',
                                }}
                            >
                                {selected.Id}
                            </div>

                            <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                {selected.Activity}
                            </span>
                        </div>

                        {(
                            [
                                ['Timestamp', moment(selected.Time).format('DD-MMM-YYYY hh:mm A')],
                                ['Source', selected.Source],
                                ['Geofence', selected.Geofence],
                                ['Distance from Geofence', distance],
                            ] as [string, string][]
                        ).map(([lbl, val]) => (
                            <div className='gf-detail-row' key={lbl}>
                                <span className='gf-detail-lbl fs-7'>{lbl}</span>

                                <span className='gf-detail-val fs-7'>{val}</span>
                            </div>
                        ))}

                        <div className='gf-detail-row'>
                            <span className='gf-detail-lbl fs-7'>Location Status</span>
                            <span>
                                {selected.Status === 'Inside' && (
                                    <span className='gf-badge-in fs-7'>Inside Geofence</span>
                                )}
                                {selected.Status === 'Outside' && (
                                    <span className='gf-badge-out fs-7'>Outside Geofence</span>
                                )}
                            </span>
                        </div>

                        <div style={{ marginTop: 12, marginBottom: 4 }}>
                            <div className='gf-value'>Location Preview</div>
                            <PreviewMap zone={zone} activity={selected} />
                        </div>

                        <div className='gf-adv-toggle' onClick={() => setAdvOpen(v => !v)}>
                            <span className='gf-value'>Advanced Details</span>

                            <span style={{ color: '#b0b3bd', fontSize: '0.78rem' }}>
                                {advOpen ? (
                                    <i className='ki-duotone ki-up fs-5'>
                                        <span className='path1' />
                                        <span className='path2' />
                                    </i>
                                ) : (
                                    <i className='ki-duotone ki-down fs-5'>
                                        <span className='path1' />
                                        <span className='path2' />
                                    </i>
                                )}
                            </span>
                        </div>

                        {advOpen && (
                            <div className='gf-adv-section'>
                                {(
                                    [
                                        ['Latitude', Number(selected.Latitude).toFixed(6)],
                                        ['Longitude', Number(selected.Longitude).toFixed(6)],
                                        ['Device Information', selected.DeviceInformation || '—'],
                                    ] as [string, string][]
                                ).map(([lbl, val]) => (
                                    <div className='gf-detail-row' key={lbl}>
                                        <span className='gf-detail-lbl fs-7'>{lbl}</span>

                                        <span className='gf-detail-val fs-7'>{val}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

const ActivityTable: React.FC<{ data: ActivityItem[], zones: ResolvedZone[], selected: ActivityItem | null, onSelect: (a: ActivityItem) => void }> = ({ data, zones, selected, onSelect }) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())
    const zoneByName = useMemo(() => new Map(zones.map(z => [z.name, z])), [zones])

    const columns = useMemo(
        () => [
            columnHelper.accessor('Time', {
                header: 'Time',
                cell: info => <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{format12HoursOnly(info.getValue(), 0)}</span>,
                enableSorting: true,
            }),
            columnHelper.accessor('Activity', {
                header: 'Activity',
                cell: info => {
                    const value = info.getValue()
                    const config: Record<string, { color: string }> = {
                        'Clock In': { color: '#50CD89' },
                        'Clock Out': { color: '#50CD89' },
                        'Break In': { color: '#3699FF' },
                        'Break Out': { color: '#F59E0B' },
                        'Overtime Start': { color: '#8B5CF6' },
                        'Overtime End': { color: '#8B5CF6' },
                    }
                    return (
                        <div className='d-flex align-items-center gap-3'>
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: config[value]?.color || '#ADB5BD',
                                    display: 'inline-block',
                                }}
                            />
                            <span>{value}</span>
                        </div>
                    )
                },
                enableSorting: true,
            }),
            columnHelper.accessor('Source', {
                header: 'Source',
                cell: info => <span style={{ color: '#6b7280' }}>{info.getValue()}</span>,
                enableSorting: true,
            }),
            columnHelper.accessor('Geofence', {
                header: 'Geofence',
                cell: info => <span className='gf-geofence-pill'>{info.getValue()}</span>,
                enableSorting: true,
            }),
            columnHelper.accessor('Distance', {
                header: () => (
                    <span>
                        Distance
                        <i className='bi bi-arrow-down-up ms-1' style={{ fontSize: '0.6rem', opacity: 0.5 }}></i>
                    </span>
                ),
                cell: info => {
                    const activity = info.row.original
                    const zone = zoneByName.get(activity.Geofence)
                    const distance = getActivityZoneDistance(activity, zone)
                    const hasValidDistance = distance != null && isFinite(distance)

                    return (
                        <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                            {hasValidDistance ? `${distance?.toFixed(2)} m` : '—'}
                        </span>
                    )
                },
                enableSorting: true,
            }),
            columnHelper.accessor('Status', {
                header: 'Status',
                cell: info => {
                    const s = info.getValue()
                    if (s === 'Inside') return <span className='gf-badge-in'>Inside Geofence</span>
                    if (s === 'Outside') return <span className='gf-badge-out'>Outside Geofence</span>
                    return <span className='gf-badge-unk'>Unknown Location</span>
                },
                enableSorting: true,
                filterFn: (row, _id, filterValue) => filterValue === 'all' || row.original.Status === filterValue,
            }),
            columnHelper.display({
                id: 'action',
                header: 'Action',
                cell: ({ row }) => (
                    <button
                        className='btn btn-sm p-0 border-0'
                        style={{ color: '#94a3b8', fontSize: '1rem' }}
                        onClick={e => {
                            e.stopPropagation()
                            onSelect(row.original)
                        }}
                        title='View details'
                    >
                        <i
                            className='bi bi-eye-fill text-gray'
                            data-tooltip-id="tooltip-description"
                            data-tooltip-content={getValueFromResource('View details', dispatch, t)}
                        ></i>
                    </button>
                ),
            }),
        ],
        [zoneByName]
    )

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    })

    return (
        <div className=''>
            <div className='my-3'>
                <div>
                    <span className='gf-value'>Attendance Location Activities</span>

                    <p className='gf-label'>
                        Each row represents a recorded attendance activity and its geofence
                        validation result.
                    </p>
                </div>
            </div>

            <div className='table-responsive'>
                <table
                    className='table table-sm mb-0 align-middle'
                    style={{
                        fontSize: '0.77rem',
                    }}
                >
                    <thead>
                        <tr
                            style={{
                                background: '#fff',
                                borderBottom: '1px solid #E9ECEF',
                            }}
                        >
                            {table.getHeaderGroups().map(hg =>
                                hg.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className='px-3'
                                        style={{
                                            padding: '10px 12px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#343A40',
                                            borderBottom: '1px solid #E9ECEF',
                                            whiteSpace: 'nowrap',
                                            background: '#F8F9FA',
                                        }}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}

                                        {header.column.getIsSorted() === 'asc' && (
                                            <i
                                                className='bi bi-sort-up ms-1'
                                                style={{
                                                    fontSize: '0.65rem',
                                                }}
                                            />
                                        )}

                                        {header.column.getIsSorted() === 'desc' && (
                                            <i
                                                className='bi bi-sort-down ms-1'
                                                style={{
                                                    fontSize: '0.65rem',
                                                }}
                                            />
                                        )}
                                    </th>
                                ))
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr
                                key={row.id}
                                ref={el => {
                                    if (el) {
                                        rowRefs.current.set(row.original.Id, el)
                                    } else {
                                        rowRefs.current.delete(row.original.Id)
                                    }
                                }}
                                className={`gf-tr ${selected?.Id === row.original.Id ? 'sel' : ''}`}
                                onClick={() => onSelect(row.original)}
                                style={{
                                    background: '#fff',
                                    borderBottom: '1px solid #E9ECEF',
                                }}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        style={{
                                            padding: '10px 12px',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}

                        {table.getRowModel().rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className='text-center text-muted py-4'
                                    style={{
                                        padding: '10px 12px',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    No Record Found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

const GeofenceModal = (props: any) => {
    const { onCloseModal, showGeofenceModal, initialActivityKey } = props
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { AccountAttendanceGeofenceLocationDetail } = useAppSelector(state => state.attendance)
    const { cultureInfoName } = useAppSelector(state => state.globalState)
    const [selected, setSelected] = useState<ActivityItem | null>(null)
    const summary: SummaryData = AccountAttendanceGeofenceLocationDetail?.[0]?.Summary
    const activities: ActivityItem[] = useMemo(
        () => AccountAttendanceGeofenceLocationDetail?.[0]?.Activities ?? [],
        [AccountAttendanceGeofenceLocationDetail]
    )

    useEffect(() => {
        if (initialActivityKey && activities.length) {
            const match = activities.find(a => `${a.AccountEmployeeAttendanceId}_${a.Time}` === initialActivityKey)
            if (match) {
                setSelected(match)
            }
        }
    }, [initialActivityKey, activities])

    const zones = useMemo(() => resolveZones(summary?.AttendanceZoneList ?? [], activities),
        [summary, activities]
    )

    const insideCount = activities.filter(a => a.Status === 'Inside').length
    const outsideCount = activities.filter(a => a.Status === 'Outside').length
    const knownCount = insideCount + outsideCount
    const computedCompliance = knownCount ? Math.round((insideCount / knownCount) * 100) : 0

    const STAT_CARDS = [
        { icon: ActivitiesRecorded, val: summary?.ActivitiesRecorded ?? activities.length, lbl: 'Activities Recorded' },
        { icon: InsideGeofence, val: summary?.InsideGeofenceCount ?? insideCount, lbl: 'Inside Geofence' },
        { icon: IOutsideGeofence, val: summary?.OutsideGeofenceCount ?? outsideCount, lbl: 'Outside Geofence' },
        { icon: ComplianceScore, val: `${summary?.ComplianceScorePercent ?? computedCompliance}%`, lbl: 'Compliance Score' },
        { icon: AttendanceSource, val: summary?.AttendanceSource ?? '—', lbl: 'Attendance Source' },
    ]

    const LEGEND_ITEMS = useMemo(
        () => [
            ...zones.map(z => ({ type: 'square' as const, color: z.color, label: z.name })),
            { type: 'dot' as const, color: '#12a05c', label: 'Inside Geofence' },
            { type: 'dot' as const, color: '#e03a3a', label: 'Outside Geofence' },
            { type: 'dot' as const, color: '#9ca3af', label: 'Unknown Location' },
            { type: 'line' as const, color: '#94a3b8', label: 'Employee Route' },
        ],
        [zones]
    )

    return (
        <>
            <div
                className='modal drawer-overlay'
                tabIndex={-1}
                id='attendanceModal'
                data-bs-backdrop='false'
                role='dialog'
                aria-hidden='true'
                style={{ zIndex: 10002, display: 'block' }}
            >
                <div
                    className='modal-dialog'
                    style={{ maxWidth: '92vw', width: '92vw', marginTop: 10 }}
                >
                    <div
                        className='modal-content'
                        style={{ borderRadius: '8px' }}
                    >
                        <div className='modal-header py-4 px-5 border-bottom'>
                            <div className='d-flex justify-content-between align-items-center w-100'>
                                <div className='d-flex align-items-center gap-5'>
                                    {showGeofenceModal && (
                                        <button
                                            type='button'
                                            className='btn btn-sm btn-light-primary'
                                            onClick={() => onCloseModal()}
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0,
                                            }}
                                        >
                                            <KTIcon iconName='left' className='fs-3' />
                                        </button>
                                    )}

                                    <div className='symbol symbol-60px'>
                                        <img src='/media/avatars/300-1.jpg' alt='employee' className='rounded-circle' />
                                    </div>

                                    <div className='d-flex align-items-center flex-wrap gap-4'>
                                        <div className='detail-item'>
                                            <div className='gf-value'>{summary?.EmployeeName}</div>

                                            <div className='text-muted fs-7'>
                                                {summary?.EmployeeCode ?? '—'}
                                            </div>
                                        </div>

                                        <div className='detail-item'>
                                            <div className='text-muted fs-8'>Department</div>

                                            <div
                                                className='gf-header-val'
                                                title={summary?.DepartmentName}
                                            >
                                                {(summary?.DepartmentName ?? '').length > 20 ? `${(summary?.DepartmentName ?? '').substring(0, 20)}...` : (summary?.DepartmentName ?? '')}
                                            </div>
                                        </div>

                                        <div className='detail-item'>
                                            <div className='text-muted fs-8'>Shift</div>

                                            <div
                                                className='gf-header-val'
                                                title={summary?.ShiftName}
                                            >
                                                {(summary?.ShiftName ?? '').length > 20 ? `${(summary?.ShiftName ?? '').substring(0, 20)}...` : (summary?.ShiftName ?? '')}
                                            </div>
                                        </div>

                                        <div className='detail-item no-border'>
                                            <div className='text-muted fs-8'>Attendance Date</div>
                                            <div className='gf-header-val'>
                                                {summary?.AttendanceDate ? formatDateWithDateAndMonthName(summary.AttendanceDate, cultureInfoName.toString()) : '—'}
                                                <KTIcon iconName='calendar' className='fs-6 ms-2' />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='d-flex align-items-center gap-3'>
                                    <div
                                        className='btn btn-sm btn-icon btn-active-light-primary'
                                        onClick={onCloseModal}
                                    >
                                        <KTIcon iconName='cross' className='fs-2' />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className='modal-body pb-4 px-4 pt-4'
                            style={{
                                overflowY: 'auto',
                                maxHeight: 'calc(99vh - 200px)',
                            }}
                        >
                            <div className='row g-3'>
                                <div className='col-12 col-xl-9 d-flex flex-column gap-3'>
                                    <div className='row g-2'>
                                        {STAT_CARDS.map(c => (
                                            <div key={c.lbl} className='gf-stat-col'>
                                                <div className='gf-stat-card'>
                                                    <div className='attendance-icon'>
                                                        <img src={c.icon} />
                                                    </div>

                                                    <div>
                                                        <div className='gf-card-value'>{c.val}</div>

                                                        <div className='gf-label'>{c.lbl}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className='gf-map-wrap'>
                                        <GeoMap
                                            zones={zones}
                                            activities={activities}
                                            selected={selected}
                                            onSelect={setSelected}
                                        />

                                        <div className='gf-legend'>
                                            <div className='gf-legend-title'>Legend</div>

                                            {LEGEND_ITEMS.map(item => (
                                                <div className='gf-legend-row' key={item.label}>
                                                    {item.type === 'dot' && (
                                                        <span
                                                            className='gf-legend-dot'
                                                            style={{ background: item.color }}
                                                        />
                                                    )}

                                                    {item.type === 'line' && (
                                                        <span
                                                            className='gf-legend-line'
                                                            style={{ background: item.color }}
                                                        />
                                                    )}

                                                    {item.type === 'square' && (
                                                        <span
                                                            style={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: 2,
                                                                background: item.color,
                                                                display: 'inline-block',
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    )}

                                                    <span>{item.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <ActivityTable
                                        data={activities}
                                        zones={zones}
                                        selected={selected}
                                        onSelect={setSelected}
                                    />
                                </div>

                                <div
                                    className='col-12 col-xl-3'
                                    style={{
                                        maxHeight: 820,
                                        overflowY: 'auto',
                                    }}
                                >
                                    <RightPanel selected={selected} zones={zones} />
                                </div>
                            </div>
                        </div>

                        <div className='modal-footer py-3 px-5 border-top bg-white'>
                            <button
                                onClick={onCloseModal}
                                type='button'
                                className='btn btn-secondary btn-sm me-2'
                                id='kt_OffCanvas'
                            >
                                {getValueFromResource('Close', dispatch, t)}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ReactTooltip
                id='tooltip-description'
                style={TOOLTIP_STYLE}
                place='top'
                delayShow={100}
                delayHide={100}
                clickable
            />
        </>
    )
}

export default GeofenceModal